import gsap from 'gsap';
import DrawSVGPlugin from 'gsap/DrawSVGPlugin';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import Dispatch from '../../core/Dispatch';
import { COMPONENT_INIT } from '../../lib/events';
import Viewport from '../../core/Viewport';

gsap.registerPlugin(DrawSVGPlugin);
gsap.registerPlugin(MotionPathPlugin);

const CMMobileModel = (el, { activeIndex = 0 }) => ({
    el,
    activeStepIndex: activeIndex,
    ns: null,
    svg: null,
    tl: null,
    dashArrayTweens: [],
    drawLineTweens: null,
    init() {
        this.ns = el.dataset.modelNamespace;
        this.svg = this.el.querySelector('svg');
        this.setScale();
        this.drawLineTweens = new WeakMap();
        this.updateGraphics();
        this.animateDashArrays();
        gsap.set(this.el, { opacity: 1 }, 0);
        Viewport.on('resize', this.onResize.bind(this));
    },
    destroy() {
        Viewport.off('resize', this.onResize.bind(this));
        this.killDashArrayTweens();
    },
    onResize() {
        this.setScale();
    },
    setScale() {
        el.style.setProperty('--scale', 1);
        const minWidth = 420;
        const { width } = this.el.getBoundingClientRect();
        let scale = width / minWidth;
        if (scale > 1) {
            scale = 1;
        }
        el.style.setProperty('--scale', scale);
    },
    getDrawLineTween(pathId, opts = {}) {
        const group = this.svg.getElementById(`${this.ns}-${pathId}`);
        if (this.drawLineTweens.has(group)) {
            return this.drawLineTweens.get(group);
        }
        const { alignOrigin = [0.5, 1] } = opts || {};
        const linePath = this.svg.getElementById(`${this.ns}-${pathId}-line`);
        const maskedPath = this.svg.querySelector(`#${this.ns}-${pathId}-mask path`);
        const arrowPath = this.svg.getElementById(`${this.ns}-${pathId}-arrow`);
        const tl = gsap.timeline({
            paused: true
        });
        tl
            .set(group, {
                opacity: 1
            }, 0)
            .fromTo(maskedPath, {
                drawSVG: '0%'
            }, {
                drawSVG: '100%',
                duration: 1,
                ease: 'Cubic.easeInOut'
            }, 0)
            .to(arrowPath, {
                motionPath: {
                    path: linePath,
                    align: linePath,
                    alignOrigin,
                    autoRotate: false
                },
                duration: 1,
                ease: 'Cubic.easeInOut'
            }, 0)
            .fromTo(arrowPath, {
                scale: 0
            }, {
                scale: 1,
                duration: 0.3,
                ease: 'Cubic.easeInOut'
            }, 0);
        this.drawLineTweens.set(group, tl);
        return this.drawLineTweens.get(group);
    },
    updateGraphics() {
        const steps = ['eu-ets', 'blending-requirements', 'esg', 'regulations', 'displacement-requirements'];
        const activeStep = steps[this.activeStepIndex];

        const tl = gsap.timeline();

        const activeCustomerElement = this.el.querySelector(`[data-element="${activeStep}"]`);
        const inactiveCustomerElements = this.el.querySelectorAll(`[data-customers] [data-element]:not([data-element="${activeStep}"])`);
        gsap.killTweensOf([activeCustomerElement, ...inactiveCustomerElements]);
        tl
            .to(inactiveCustomerElements, {
                opacity: 0,
                duration: 0.3
            }, 0)
            .to(inactiveCustomerElements, {
                scale: 0.85,
                duration: 0.3,
                ease: 'Back.easeIn',
                transformOrigin: 'center bottom'
            }, 0)
            .to(inactiveCustomerElements, {
                yPercent: -10,
                duration: 0.3,
                ease: 'Back.easeIn'
            }, 0)
            .fromTo(activeCustomerElement, {
                opacity: 0
            }, {
                opacity: 1,
                duration: 0.3
            }, 0.3)
            .fromTo(activeCustomerElement, {
                scale: 0.85
            }, {
                scale: 1,
                ease: 'Back.easeOut',
                transformOrigin: 'center bottom'
            }, 0.3)
            .fromTo(activeCustomerElement, {
                yPercent: 10
            }, {
                yPercent: 0,
                ease: 'Back.easeOut'
            }, 0.3);

        // Show GoO? (yes, always the first time)
        const gooElement = this.el.querySelector('[data-element="goo"]');
        if (!gooElement.classList.contains('initialised')) {
            tl
                .fromTo(gooElement, {
                    scale: 0.85
                }, {
                    scale: 1,
                    ease: 'Back.easeOut'
                }, 0.4)
                .fromTo(gooElement, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.3
                }, 0.4);
            gooElement.classList.add('initialised');
        }

        // Move GoO?
        tl
            .to(gooElement, {
                xPercent: this.activeStepIndex >= 2 ? -50 : 0,
                duration: 0.5,
                ease: 'Back.easeInOut'
            }, 0.3);

        // Show PoS?
        const posElement = this.el.querySelector('[data-element="pos"]');
        if (this.activeStepIndex >= 2 && this.activeStepIndex <= 3 && !posElement.classList.contains('initialised')) {
            tl
                .fromTo(posElement, {
                    scale: 0.85
                }, {
                    scale: 1,
                    ease: 'Back.easeOut'
                }, 0.4)
                .fromTo(posElement, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.3
                }, 0.4);
            posElement.classList.add('initialised');
        } else if ((this.activeStepIndex < 2 || this.activeStepIndex >= 4) && posElement.classList.contains('initialised')) {
            tl
                .to(posElement, {
                    scale: 0.85,
                    ease: 'Back.easeOut'
                }, 0.4)
                .to(posElement, {
                    opacity: 0,
                    duration: 0.3
                }, 0.4);
            posElement.classList.remove('initialised');
        }

        // Show Nabisy?
        const nabisyElement = this.el.querySelector('[data-element="nabisy"]');
        if (this.activeStepIndex === 4 && !nabisyElement.classList.contains('initialised')) {
            tl
                .fromTo(nabisyElement, {
                    scale: 0.85
                }, {
                    scale: 1,
                    ease: 'Back.easeOut'
                }, 0.4)
                .fromTo(nabisyElement, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.3
                }, 0.4);
            nabisyElement.classList.add('initialised');
        } else if (this.activeStepIndex !== 4 && nabisyElement.classList.contains('initialised')) {
            tl
                .to(nabisyElement, {
                    scale: 0.85,
                    ease: 'Back.easeOut'
                }, 0.4)
                .to(nabisyElement, {
                    opacity: 0,
                    duration: 0.3
                }, 0.4);
            nabisyElement.classList.remove('initialised');
        }

        // Show Biogas Plants? (yes, always the first time)
        const biogasElement = this.el.querySelector('[data-element="biogas-plants"]');
        if (!biogasElement.classList.contains('initialised')) {
            tl
                .fromTo(biogasElement, {
                    scale: 0.85
                }, {
                    scale: 1,
                    ease: 'Back.easeOut'
                }, 0.5)
                .fromTo(biogasElement, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.3
                }, 0.5);
            biogasElement.classList.add('initialised');
        }

        // Create/get draw line tweens
        const drawLineTweens = {
            'a1-1': this.getDrawLineTween('a1-1'),
            'a1-2': this.getDrawLineTween('a1-2'),
            'a2-1': this.getDrawLineTween('a2-1'),
            'a2-2': this.getDrawLineTween('a2-2', {
                alignOrigin: [0, 0.5]
            }),
            'a2-3': this.getDrawLineTween('a2-3'),
            'a2-4': this.getDrawLineTween('a2-4')
        };

        gsap.killTweensOf(Object.values(drawLineTweens));

        // Show/hide active lines
        if (this.activeStepIndex <= 1) {
            tl.to([
                drawLineTweens['a1-1'],
                drawLineTweens['a1-2']
            ], {
                progress: 1,
                duration: 0.5,
                ease: 'none'
            }, 0.5);
            tl.to([
                drawLineTweens['a2-1'],
                drawLineTweens['a2-2'],
                drawLineTweens['a2-3'],
                drawLineTweens['a2-4']
            ], {
                progress: 0,
                duration: 0.5,
                ease: 'none'
            }, 0);
        } else {
            tl.to([
                drawLineTweens['a2-1'],
                drawLineTweens['a2-2'],
                drawLineTweens['a2-3'],
                drawLineTweens['a2-4']
            ], {
                progress: 1,
                duration: 0.75,
                ease: 'none'
            }, 0.5);
            tl.to([
                drawLineTweens['a1-1'],
                drawLineTweens['a1-2']
            ], {
                progress: 0,
                duration: 0.5,
                ease: 'none'
            }, 0);
        }
    },
    setActiveStepIndex(index) {
        this.activeStepIndex = index;
        this.updateGraphics();
    },
    animateDashArrays() {
        this.killDashArrayTweens();
        this.svg.querySelectorAll('path[id$="line"]')
            .forEach(path => {
                const length = MotionPathPlugin.getLength(path);
                const tween = gsap.to(path, {
                    strokeDashoffset: -length,
                    duration: length / 20,
                    repeat: -1,
                    ease: 'none'
                }, 0);
                this.dashArrayTweens.push(tween);
            });
    },
    killDashArrayTweens() {
        this.dashArrayTweens.forEach(tween => {
            tween.kill();
        });
        this.dashArrayTweens = [];
    }
});

const CMDesktopModel = (el, { activeIndex = 0 }) => ({
    el,
    activeStepIndex: activeIndex,
    dashArrayTweens: [],
    ns: null,
    svg: null,
    init() {
        this.ns = el.dataset.modelNamespace;
        this.svg = this.el.querySelector('svg');
        Viewport.on('resize', this.onResize.bind(this));
        this.activatePaths();
        this.animateDashArrays();
        this.setScale();
        this.intro();
    },
    destroy() {
        this.killDashArrayTweens();
        Viewport.off('resize', this.onResize.bind(this));
    },
    onResize() {
        this.setScale();
    },
    setScale() {
        const minWidth = 1280;
        const { width } = el.getBoundingClientRect();
        let scale = width / minWidth;
        if (scale > 1) {
            scale = 1;
        }
        el.style.setProperty('--scale', scale);
    },
    deactivateAllPaths() {
        this.svg.querySelectorAll(`g[id^="${this.ns}-a"]`)
            .forEach(group => {
                group.classList.remove('active');
            });
        this.svg.querySelectorAll('path[id$="line-active"]')
            .forEach(path => {
                path.classList.remove('active');
            });
    },
    activatePath(pathId) {
        const group = this.svg.getElementById(`${this.ns}-${pathId}`);
        group.classList.add('active');
        const activeLine = this.svg.getElementById(`${this.ns}-${pathId}-line-active`);
        activeLine.classList.add('active');
    },
    activatePaths() {
        this.deactivateAllPaths();

        const activePathsMap = [[
            'a1', 'a2'
        ], [
            'a2', 'a3'
        ], [
            'a2', 'a3', 'a4', 'a5', 'a7'
        ], [
            'a2', 'a6', 'a7', 'a11'
        ], [
            'a7', 'a10', 'a8', 'a9'
        ]];

        activePathsMap[this.activeStepIndex].forEach(pathId => {
            this.activatePath(pathId);
        });
    },
    setActiveStepIndex(index) {
        this.activeStepIndex = index;
        this.activatePaths();
    },
    animateDashArrays() {
        this.killDashArrayTweens();
        this.svg.querySelectorAll('path[id$="line-active"]')
            .forEach(path => {
                const dashArray = path.getAttribute('stroke-dasharray').split(',');
                const dash = parseInt(dashArray[0], 10);
                const gap = parseInt(dashArray[1], 10);
                const dashPattern = dash + gap;
                const length = MotionPathPlugin.getLength(path);
                // Make strokeDasharray repeat evenly along the path
                const repeatCount = Math.ceil(length / dashPattern);
                const adjustedLength = repeatCount * dashPattern;
                gsap.set(path, {
                    strokeDasharray: `${dash} ${gap}`,
                    strokeDashoffset: 0
                });
                const tween = gsap.to(path, {
                    strokeDashoffset: -adjustedLength,
                    duration: adjustedLength / 20,
                    repeat: -1,
                    ease: 'none'
                }, 0);
                this.dashArrayTweens.push(tween);
            });
    },
    killDashArrayTweens() {
        this.dashArrayTweens.forEach(tween => {
            tween.kill();
        });
        this.dashArrayTweens = [];
    },
    getDrawLineTween(pathId, opts = {}) {
        const linePath = this.svg.getElementById(`${this.ns}-${pathId}-line`);
        const lineArrow = this.svg.getElementById(`${this.ns}-${pathId}-arrow`);
        const {
            duration = 1,
            autoRotate = 90,
            alignOrigin = [0.5, 1]
        } = opts;
        const tl = gsap.timeline({ paused: true });
        tl.fromTo(linePath, {
            drawSVG: '0%'
        }, {
            drawSVG: '100%',
            ease: 'none',
            duration
        }, 0);
        tl.to(lineArrow, {
            motionPath: {
                path: linePath,
                align: linePath,
                alignOrigin,
                autoRotate
            },
            ease: 'none',
            duration
        }, 0);
        tl.add(() => {
            linePath.closest('g')
                .classList
                .add('initialised');
            const activeLinePath = this.svg.getElementById(`${this.ns}-${pathId}-line-active`);
            activeLinePath.classList.add('initialised');
        }, '-=0.1');
        return tl;
    },
    getScaleArrowTween(pathId) {
        return gsap.fromTo(`#${this.ns}-${pathId}-arrow`, {
            scale: 0
        }, {
            scale: 1,
            duration: 0.3,
            ease: 'Cubic.easeInOut'
        }, 0);
    },
    getElementTween(elementId) {
        const element = document.getElementById(`element-${this.ns}-${elementId}`);
        const tl = gsap.timeline()
            .fromTo(element, {
                scale: 0.5
            }, {
                scale: 1,
                duration: 0.2,
                ease: 'Quad.easeOut',
                transformOrigin: 'bottom center'
            }, 0)
            .fromTo(element, {
                opacity: 0,
                scale: 0
            }, {
                opacity: 1,
                duration: 0.15
            }, 0);
        const subtitle = element.querySelector('[data-subtitle]');
        if (subtitle) {
            tl
                .fromTo(subtitle, {
                    y: -5
                }, {
                    y: 0,
                    duration: 0.15,
                    ease: 'Quad.easeOut'
                }, 'subtitle-=0.1')
                .fromTo(subtitle, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.3
                }, 'subtitle-=0.1');
        }
        return tl;
    },
    getHeadingTween(headingId) {
        const heading = this.el.querySelectorAll(`[data-heading="${headingId}"]`);
        return gsap.timeline()
            .fromTo(heading, {
                x: 20
            }, {
                x: 0,
                ease: 'Quad.easeOut',
                duration: 0.3
            }, 0)
            .fromTo(heading, {
                opacity: 0
            }, {
                opacity: 1,
                duration: 0.1
            }, 0);
    },
    intro() {
        const tl = gsap.timeline({
            paused: true
        });

        tl.set(this.el, { opacity: 1 }, 0);

        tl.add(this.getElementTween('biogas-plants'), 0);
        tl.add(this.getElementTween('goo'), 0);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a2'), 0)
            .to([
                this.getDrawLineTween('a2')
            ], {
                progress: 1,
                duration: 0.3,
                ease: 'Cubic.easeInOut'
            }, 0), 0.1);

        tl.add(this.getElementTween('eu-ets'), 0.3);
        tl.add(this.getElementTween('blending-requirements'), 0.4);
        tl.add(this.getElementTween('esg'), 0.5);
        tl.add(this.getElementTween('regulations'), 0.6);
        tl.add(this.getElementTween('displacement-requirements'), 0.7);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a1'), 0)
            .to(this.getDrawLineTween('a1'), {
                progress: 1,
                duration: 0.3,
                ease: 'Cubic.easeInOut'
            }, 0), 0.14);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a3'), 0)
            .to(this.getDrawLineTween('a3'), {
                progress: 1,
                duration: 0.3,
                ease: 'Cubic.easeInOut'
            }, 0), 0.15);

        tl.add(this.getElementTween('pos'), 0.2);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a6'), 0)
            .to(this.getDrawLineTween('a6'), {
                progress: 1,
                duration: 0.6,
                ease: 'Cubic.easeInOut'
            }, 0), 0.1);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a7'), 0)
            .to(this.getDrawLineTween('a7'), {
                progress: 1,
                duration: 0.45,
                ease: 'Cubic.easeInOut'
            }, 0), 0);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a11'), 0)
            .to(this.getDrawLineTween('a11'), {
                progress: 1,
                duration: 0.25,
                ease: 'Cubic.easeInOut'
            }, 0), 0.45);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a9'), 0)
            .to(this.getDrawLineTween('a9', {
                autoRotate: false,
                alignOrigin: [0, 0.5]
            }), {
                progress: 1,
                duration: 0.25,
                ease: 'Cubic.easeInOut'
            }, 0), 0.3);

        tl.add(this.getElementTween('nabisy'), 0.4);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a5'), 0)
            .to(this.getDrawLineTween('a5'), {
                progress: 1,
                duration: 0.5,
                ease: 'Cubic.easeInOut'
            }, 0), 0.25);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a10'), 0)
            .to(this.getDrawLineTween('a10'), {
                progress: 1,
                duration: 0.5,
                ease: 'Cubic.easeInOut'
            }, 0), 0.35);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a4'), 0.1)
            .to(this.getDrawLineTween('a4'), {
                progress: 1,
                duration: 0.45,
                ease: 'Cubic.easeInOut'
            }, 0), 0.3);

        tl.add(gsap.timeline()
            .add(this.getScaleArrowTween('a8'), 0.1)
            .to(this.getDrawLineTween('a8'), {
                progress: 1,
                duration: 0.5,
                ease: 'Cubic.easeInOut'
            }, 0), 0.35);

        tl.add(this.getHeadingTween('bigadan'), 0);
        tl.add(this.getHeadingTween('system'), 0.1);
        tl.add(this.getHeadingTween('customers'), 0.2);

        gsap.to(tl, {
            progress: 1,
            ease: 'none',
            duration: 3
        });
    }
});

export default el => {

    const totalSteps = el.querySelectorAll('[data-content]').length;
    const desktopModelEl = el.querySelector('[data-model="ls"]');

    let desktopModel;
    let desktopModelObserver;

    const mobileModelEl = el.querySelector('[data-model="pt"]');
    let mobileModel;
    let mobileModelObserver;

    const getActiveStepIndex = () => {
        const contentNodes = el.querySelectorAll('[data-content]');
        const activeContent = el.querySelector('[data-content]:not([inert])');
        return Math.max([...contentNodes].indexOf(activeContent), 0);
    };

    const getStepIdByIndex = index => [...el.querySelectorAll('[data-content]')][index].id;

    const updateCounter = () => {
        el.querySelector('[data-current]').textContent = getActiveStepIndex() + 1;
    };

    const setActiveStep = id => {
        const activePanel = document.getElementById(id);
        if (!activePanel.inert) {
            return;
        }
        el.querySelectorAll('button[aria-controls][aria-current]')
            .forEach(button => {
                button.removeAttribute('aria-current');
            });
        el.querySelectorAll(`button[aria-controls="${id}"]`)
            .forEach(button => {
                button.setAttribute('aria-current', 'true');
            });
        const inactivePanels = el.querySelectorAll(`[data-content]:not([id="${id}"]):not([inert])`);
        inactivePanels
            .forEach(content => {
                content.inert = true;
            });
        activePanel.inert = false;
        const tl = gsap.timeline()
            .timeScale(1.25);

        if (inactivePanels.length) {
            inactivePanels.forEach(panel => {
                tl
                    .set(panel, {
                        visibility: 'visible'
                    }, 'out')
                    .to(panel, {
                        opacity: 0,
                        duration: 0.5,
                        ease: 'Cubic.easeIn'
                    }, 'out')
                    .to(panel, {
                        y: -5,
                        duration: 0.5,
                        ease: 'Quint.easeIn'
                    }, 'out')
                    .set(panel, {
                        clearProps: 'visibility'
                    });
            });
        }
        tl
            .set(activePanel, {
                clearProps: 'visibility'
            }, 'in-=0.15')
            .fromTo(activePanel, {
                opacity: 0
            }, {
                opacity: 1,
                duration: 0.5,
                ease: 'Cubic.easeIn'
            }, 'in-=0.15')
            .fromTo(activePanel, {
                y: 5
            }, {
                y: 0,
                duration: 0.75,
                ease: 'Quint.easeOut'
            }, 'in-=0.15');

        updateCounter();
        if (desktopModel) {
            desktopModel.setActiveStepIndex(getActiveStepIndex());
        }
        if (mobileModel) {
            mobileModel.setActiveStepIndex(getActiveStepIndex());
        }
    };

    const onModelElementClick = e => {
        const id = e.target.getAttribute('aria-controls');
        setActiveStep(id);
    };

    const onPrevBtnClick = () => {
        let activeStepIndex = getActiveStepIndex();
        if (!activeStepIndex) {
            activeStepIndex = totalSteps - 1;
        } else {
            activeStepIndex -= 1;
        }
        setActiveStep(getStepIdByIndex(activeStepIndex));
    };

    const onNextBtnClick = () => {
        let activeStepIndex = getActiveStepIndex();
        if (activeStepIndex >= totalSteps - 1) {
            activeStepIndex = 0;
        } else {
            activeStepIndex += 1;
        }
        setActiveStep(getStepIdByIndex(activeStepIndex));
    };

    const init = () => {
        desktopModelEl.querySelectorAll('button[aria-controls]')
            .forEach(button => {
                button.addEventListener('click', onModelElementClick);
            });

        el.querySelector('button[data-nav="prev"]')
            .addEventListener('click', onPrevBtnClick);
        el.querySelector('button[data-nav="next"]')
            .addEventListener('click', onNextBtnClick);

        mobileModelObserver = new IntersectionObserver(([{ isIntersecting }]) => {
            if (!isIntersecting) {
                return;
            }
            mobileModelObserver.disconnect();
            mobileModelObserver = null;
            mobileModel = CMMobileModel(mobileModelEl, { activeIndex: getActiveStepIndex() });
            mobileModel.init();
        });
        mobileModelObserver.observe(mobileModelEl);

        desktopModelObserver = new IntersectionObserver(([{ isIntersecting }]) => {
            if (!isIntersecting) {
                return;
            }
            desktopModelObserver.disconnect();
            desktopModelObserver = null;
            desktopModel = CMDesktopModel(desktopModelEl, { activeIndex: getActiveStepIndex() });
            desktopModel.init();
        });
        desktopModelObserver.observe(desktopModelEl);

        Dispatch.emit(COMPONENT_INIT);
    };

    const destroy = () => {
        desktopModelEl.querySelectorAll('button[aria-controls]')
            .forEach(button => {
                button.removeEventListener('click', onModelElementClick);
            });

        el.querySelector('button[data-nav="prev"]')
            .removeEventListener('click', onPrevBtnClick);
        el.querySelector('button[data-nav="next"]')
            .removeEventListener('click', onNextBtnClick);

        if (mobileModelObserver) {
            mobileModelObserver.disconnect();
            mobileModelObserver = null;
        }

        if (mobileModel) {
            mobileModel.destroy();
            mobileModel = null;
        }

        if (desktopModelObserver) {
            desktopModelObserver.disconnect();
            desktopModelObserver = null;
        }

        if (desktopModel) {
            desktopModel.destroy();
            desktopModel = null;
        }
    };

    return {
        init,
        destroy
    };

};
